import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { homeSummaryApi, NetworkHealthSummary } from "../../../../api/homeSummaryApi";
import { ROUTES } from "../../../../config/routes";
import { buildQueryParams } from "../../../../utils/urlFilters";
import {
  claimsAdsFilterOptions,
  metadataFilterOptions,
} from "../../../YouTubeScorecard/Objects/multiSelectObjects";
import { WidgetFilterValues } from "../../prefs/homePrefs";
import {
  MANAGERS_KEY,
  WidgetComponentProps,
  getScope,
  getStringArray,
  scopeParams,
} from "../types";
import { useWidgetData } from "../useWidgetData";
import {
  BarItem,
  CardEmpty,
  CardError,
  CardLoading,
  Freshness,
  IssueBars,
  Kpi,
  KpiGrid,
  countTone,
} from "../ui";

const flattenLabels = (items: any[]): Record<string, string> =>
  items.reduce((acc, item) => {
    if (item.children) Object.assign(acc, flattenLabels(item.children));
    else acc[item.key] = item.label;
    return acc;
  }, {} as Record<string, string>);

const METADATA_LABELS: Record<string, string> = {
  ...flattenLabels(metadataFilterOptions),
  missingTitle: "Missing title",
  missingDescription: "Description",
};
const CLAIMS_LABELS = flattenLabels(claimsAdsFilterOptions);

const CONTENT_FORMAT_LABELS: Record<string, string> = {
  VIDEO_ON_DEMAND: "Video",
  SHORTS: "Shorts",
  UNSPECIFIED: "Unspecified",
};

/** Deep link into the scorecard tab with the card's filters applied. */
export const scorecardLink = (
  base: string,
  filters: WidgetFilterValues,
  extra: Record<string, string[] | undefined> = {}
): string => {
  const scope = getScope(filters);
  const params = buildQueryParams({
    myIssuesOnly: scope === "mine",
    channelManager: scope === "managers" ? getStringArray(filters, MANAGERS_KEY) : undefined,
    legalEntityType: getStringArray(filters, "legalEntityTypes"),
    contentFormat: getStringArray(filters, "contentFormats"),
    ...extra,
  });
  const query = params.toString();
  return query ? `${base}?${query}` : base;
};

const NetworkHealthCard: React.FC<WidgetComponentProps> = ({ filters, onOptions }) => {
  const categories = getStringArray(filters, "categories");
  const requestParams = useMemo(
    () => ({
      ...scopeParams(filters),
      categories: categories.join(","),
      legalEntityTypes: getStringArray(filters, "legalEntityTypes").join(","),
      contentFormats: getStringArray(filters, "contentFormats").join(","),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(filters)]
  );

  const { data, loading, error, reload } = useWidgetData<NetworkHealthSummary>(
    `network-health:${JSON.stringify(requestParams)}`,
    () => homeSummaryApi.getNetworkHealth(requestParams)
  );

  useEffect(() => {
    if (!data) return;
    onOptions(
      "legalEntityTypes",
      data.options.legalEntityTypes.map((v) => ({ label: v, value: v }))
    );
    onOptions(
      "contentFormats",
      data.options.contentFormats.map((v) => ({ label: CONTENT_FORMAT_LABELS[v] ?? v, value: v }))
    );
  }, [data, onOptions]);

  if (loading && !data) return <CardLoading rows={3} />;
  if (error) return <CardError message={error} onRetry={reload} />;
  if (!data) return null;

  const metaTotal = data.metadata?.total ?? 0;
  const claimsTotal = data.claims?.total ?? 0;
  const total = metaTotal + claimsTotal;
  const scope = getScope(filters);

  const topIssues: BarItem[] = [
    ...Object.entries(data.metadata?.byIssue ?? {}).map(([key, count]) => ({
      label: METADATA_LABELS[key] ?? key,
      count,
      to: scorecardLink(ROUTES.YOUTUBE_SCORECARD_METADATA, filters, { issue: [key] }),
    })),
    ...Object.entries(data.claims?.byIssue ?? {}).map(([key, count]) => ({
      label: CLAIMS_LABELS[key] ?? key,
      count,
      to: scorecardLink(ROUTES.YOUTUBE_SCORECARD_CLAIMS_ADS, filters, { issue: [key] }),
    })),
  ]
    .filter((i) => i.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  if (total === 0 && scope === "mine") {
    return (
      <CardEmpty
        title="No open issues on your channels"
        body="Nothing in the scorecard is assigned to you as channel manager."
        action={
          <Link className="home-link" to={scorecardLink(ROUTES.YOUTUBE_SCORECARD_METADATA, { scope: "all" })}>
            View all network issues
          </Link>
        }
      />
    );
  }

  return (
    <>
      <KpiGrid columns={3}>
        <Kpi label="Open issues" value={total} tone={countTone(total)} emphasis />
        {data.metadata && (
          <Kpi
            label="Metadata & Properties"
            value={metaTotal}
            tone={countTone(metaTotal)}
            to={scorecardLink(ROUTES.YOUTUBE_SCORECARD_METADATA, filters)}
          />
        )}
        {data.claims && (
          <Kpi
            label="Claims & Ads"
            value={claimsTotal}
            tone={countTone(claimsTotal)}
            to={scorecardLink(ROUTES.YOUTUBE_SCORECARD_CLAIMS_ADS, filters)}
          />
        )}
      </KpiGrid>
      <IssueBars title="Top issue types" items={topIssues} emptyText="No issues for these filters" />
      <div className="home-card__meta">
        {data.musicLabelIssues > 0 && (
          <Link
            className="home-link"
            to={scorecardLink(ROUTES.YOUTUBE_SCORECARD_MOONBUG_MUSIC_LABEL, filters)}
          >
            <i className="pi pi-volume-up" /> {data.musicLabelIssues.toLocaleString("en-GB")} Moonbug Music Label
          </Link>
        )}
        <Freshness label="Scorecard data" at={data.dataUpdatedAt} />
      </div>
    </>
  );
};

export default NetworkHealthCard;
